import { startOfDay } from "date-fns";
import { fromUnixTime } from "date-fns";
import { getUnixTime } from "date-fns";
import {
  DealActivityFragmentFragment,
} from "../../graphql/generated";
import {
  groupActivities,
  ActivityGroup,
} from "../../utils/activityGrouping";
import { ActivityDateHeader } from "./ActivityDateHeader";
import { GroupedActivityItem } from "./GroupedActivityItem";
import { ActivityItemMode } from "./ActivityItem";

export type FeedItem =
  | { type: "date-header"; date: number }
  | { type: "group"; group: ActivityGroup };

interface DealActivityFeedProps {
  activities: DealActivityFragmentFragment[];
  mode?: ActivityItemMode;
}

/**
 * Builds the feed structure: groups activities sequentially, then inserts
 * day separation headers at day boundaries.
 */
function buildFeedItems(activities: DealActivityFragmentFragment[]): FeedItem[] {
  const groups = groupActivities(activities);
  const items: FeedItem[] = [];

  let lastDayStart: number | null = null;

  for (const group of groups) {
    const firstActivity = group.activities[0];
    const dayStart = getUnixTime(
      startOfDay(fromUnixTime(firstActivity.createdAt)),
    );

    if (lastDayStart === null || dayStart !== lastDayStart) {
      items.push({ type: "date-header", date: dayStart });
      lastDayStart = dayStart;
    }

    items.push({ type: "group", group });
  }

  return items;
}

export function DealActivityFeed({
  activities,
  mode = "verbose",
}: DealActivityFeedProps) {
  if (activities.length === 0) {
    return null;
  }

  const feedItems = buildFeedItems(activities);
  const groupItems = feedItems.filter(
    (item): item is { type: "group"; group: ActivityGroup } =>
      item.type === "group",
  );
  const lastGroupIndex = groupItems.length - 1;
  let currentGroupIndex = 0;

  return (
    <ul role="list" className="space-y-2 ">
      {feedItems.map((item) => {
        if (item.type === "date-header") {
          return (
            <ActivityDateHeader key={`date-${item.date}`} date={item.date} />
          );
        }

        const isLast = currentGroupIndex === lastGroupIndex;
        const isFirst = currentGroupIndex === 0;
        currentGroupIndex++;

        return (
          <GroupedActivityItem
            key={item.group.activities.map((a) => a.id).join("-")}
            group={item.group}
            isFirst={isFirst}
            isLast={isLast}
            mode={mode}
          />
        );
      })}
    </ul>
  );
}
